const PREFIX = "claude-account-continuation-v1:";
const LEGACY = "claude:home:";
/** A native account's connection id, or a configured home's directory name. */
const ACCOUNT_ID = /^[A-Za-z0-9._-]+$/;

/**
 * A continuation key names the accounts directory a Claude home lives in and
 * the home's own id inside it. The server declares the directory when it
 * mints the key (native accounts and configured homes both carry it); a
 * legacy `claude:home:<path>` key only implies one from the path's parent.
 */
export interface ClaudeContinuationScope {
  readonly directory: string;
  readonly id: string;
  /** True when the server declared the accounts directory (v1 key); false for a legacy path key. */
  readonly declared: boolean;
}

export function claudeAccountContinuationKey(
  accountsDirectory: string,
  connectionId: string,
): string {
  return `${PREFIX}${encodeURIComponent(accountsDirectory)}:${connectionId}`;
}

export function parseClaudeContinuationKey(
  key: string | undefined | null,
): ClaudeContinuationScope | null {
  if (!key) return null;
  if (key.startsWith(PREFIX)) {
    const body = key.slice(PREFIX.length);
    const split = body.lastIndexOf(":");
    if (split <= 0) return null;
    const id = body.slice(split + 1);
    if (!ACCOUNT_ID.test(id)) return null;
    try {
      return { directory: decodeURIComponent(body.slice(0, split)), id, declared: true };
    } catch {
      return null;
    }
  }
  if (key.startsWith(LEGACY)) {
    const home = key.slice(LEGACY.length);
    const slash = home.lastIndexOf("/");
    if (slash <= 0 || slash === home.length - 1) return null;
    const id = home.slice(slash + 1);
    if (!ACCOUNT_ID.test(id)) return null;
    return { directory: home.slice(0, slash), id, declared: false };
  }
  return null;
}

/**
 * SAU5 / SAU14: a conversation may move between two Claude homes that share
 * one accounts directory, native or configured, when at least one side is a
 * server-declared key vouching for that directory. Two legacy path keys never
 * transfer on a shared parent alone (any two folders share one); a legacy key
 * still meets the declared key of the same home or a sibling, so threads
 * bound before the server declared directories keep their account and gain
 * its siblings.
 */
export function canTransferClaudeAccountContinuation(
  left: string | undefined | null,
  right: string | undefined | null,
): boolean {
  const a = parseClaudeContinuationKey(left);
  const b = parseClaudeContinuationKey(right);
  return a !== null && b !== null && a.directory === b.directory && (a.declared || b.declared);
}

export function compatibleProviderContinuation(
  left: string | undefined | null,
  right: string | undefined | null,
): boolean {
  return Boolean(
    left && right && (left === right || canTransferClaudeAccountContinuation(left, right)),
  );
}
